import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { Alert } from '@bai/components';
import { axios } from '@bai/data';
import { useUser } from '@bai/features/user';
import { useTheme } from '@bai/utils/theme';
import { TimesInMilliseconds } from '@bai/utils/types';

import AppIcons from './app-icons/app-icons';
import AppMenu from './app-menu/app-menu';

interface Params {
  sub?: string;
  givenName?: string;
  surname?: string;
  email?: string;
  username?: string;
}

function getUserPlmAlerts() {
  const alertsCallPath = 'api/user/getplmalerts';
  return axios
    .get<number>(alertsCallPath)
    .then((response) => response.data)
    .catch((error) => {
      return 0;
    });
}

export function handlePolicyManagerClick(
  user: ReturnType<typeof useUser>,
): Promise<void> {
  const payload: Params = {};
  const tokenCallPath = 'api/user/getsignedjwt';

  if (user?.id) {
    payload.sub = user?.id.toString();
  }
  if (user?.firstName) {
    payload.givenName = user?.firstName;
  }
  if (user?.lastName) {
    payload.surname = user?.lastName;
  }
  if (user?.email) {
    payload.email = user?.email;
  }
  if (user?.userName) {
    payload.username = user?.userName;
  }

  return axios
    .post(tokenCallPath, payload)
    .then((response) => {
      const signedToken = response.data as string;
      const env = window.location.hostname;
      let redirectUrl = '';
      if (env) {
        // Use endsWith with precise domain suffixes to prevent hostname-spoofing bypass.
        // The production catch-all is checked first; more-specific checks below override it.
        if (env === 'bai.org' || env.endsWith('.bai.org')) {
          redirectUrl = 'https://policy.bai.org/lms_router';
        }
        if (/^lmgr-dev\d+\.bai\.org$/.test(env)) {
          redirectUrl = 'https://plm-dev-app1.bai.org/lms_router';
        }
        if (/^lmgr-uat\d+\.bai\.org$/.test(env)) {
          redirectUrl = 'https://plm-qa-app1.bai.org/lms_router';
        }
        if (/^lmgr-qa\d+\.bai\.org$/.test(env)) {
          redirectUrl = 'https://plm-qa-app1.bai.org/lms_router';
        }
        if (env === 'localhost') {
          redirectUrl = 'https://plm-dev-app1.bai.org/lms_router';
        }
      }

      if (!redirectUrl) {
        return;
      }
      window.location.href = `${redirectUrl}?id_token=${signedToken}`;
    })
    .catch((error: unknown) => {
      console.error('Failed to redirect to Policy Manager:', error);
      throw error;
    });
}

export function ApplicationLibraryMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [numAlerts, setNumAlerts] = useState(0);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const theme = useTheme();
  const mainColor = theme?.branding?.primaryColor || theme.palette.primary.main;
  const user = useUser();
  const [plmError, setPlmError] = useState(false);

  useEffect(() => {
    getUserPlmAlerts()
      .then((alerts: number) => {
        if (alerts) {
          return setNumAlerts(alerts);
        }
        return setNumAlerts(0);
      })
      .catch((e: AxiosError) => {
        console.error(e.message);
        setNumAlerts(0);
      });
  }, []);

  const handlePolicyManagerClickInner = () => {
    setPlmError(false);
    handlePolicyManagerClick(user).catch(() => {
      setPlmError(true);
      setTimeout(() => setPlmError(false), TimesInMilliseconds.THREE_SECONDS);
    });
  };

  const appLibraryMenuItems = [
    { label: 'App Library', isTitle: true, onClick: () => {} },
    { label: 'Learning Manager', isTitle: false, onClick: () => {} },
    {
      label: 'Policy Manager',
      isTitle: false,
      onClick: handlePolicyManagerClickInner,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {plmError && (
        <Alert severity="error" data-cy="plm-redirect-error">
          Policy Manager is currently unavailable. Please try again in a few
          minutes.
        </Alert>
      )}
      <button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        style={{
          display: 'flex',
          position: 'absolute',
          left: '70vw',
          backgroundColor: 'white',
          color: 'black',
          border: '.1px solid black',
          borderBottomLeftRadius: '5px',
          borderBottomRightRadius: '5px',
          minWidth: '225px',
          height: '40px',
          fontSize: '15px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
        type="button"
        data-cy="Application Library Button"
      >
        <AppIcons count={numAlerts} mainColor={mainColor} />
        <span
          style={{
            fontFamily: 'Calibri, sans-serif',
            fontSize: '17px',
            marginTop: '6px',
            fontWeight: 'normal',
            color: 'black',
          }}
        >
          ProSight Learning Manager
        </span>
      </button>
      <AppMenu
        anchorEl={anchorEl}
        open={open}
        handleClose={handleClose}
        appLibraryMenuItems={appLibraryMenuItems}
        count={numAlerts}
      />
    </div>
  );
}

export default ApplicationLibraryMenu;
